import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import LandingLayout from "../components/layout/LandingLayout";
import HowItWorks from "../components/lp/HowItWorks";
import ManagedContentSections from "../components/lp/ManagedContentSections";
import { getUtmParams, getVisitorId, setLandingAttribution, setupPageTimeTracking, trackButtonClick, trackLandingEvent } from "../lib/analytics";
import { DEFAULT_LANDING_PAGE_CONTENT, type LandingPageContent } from "@shared/landingPages";

const LP_PATH = "/lp/drug-sourcing";
const META_TITLE = "Drug Sourcing and Free DSCSA Review | Premier Pharma";
const META_DESCRIPTION = "Drugs from authorized suppliers at a competitive price, plus a free DSCSA readiness review for licensed clinics, med spas and pharmacies. No contract required.";

const buyerTypes = [
  { label: "Independent pharmacy", value: "pharmacy" },
  { label: "Med spa / aesthetics", value: "medspa" },
  { label: "Physician practice", value: "doctors_office" },
  { label: "Specialty clinic", value: "clinic" },
  { label: "Dental / oral surgery", value: "dental" },
  { label: "Surgery center", value: "surgery_center" },
];

const risks = [
  {
    number: "01",
    title: "Patient safety",
    subtitle: "Gray-market offers",
    body: "FDA has warned that counterfeit Botox reached U.S. doctors’ offices and clinics through an unlicensed supplier. A price that looks too good from a seller you can’t verify is how it gets in the door.",
    source: "https://www.fda.gov/drugs/drugsafety/ucm443217.htm",
    sourceLabel: "FDA safety communication",
  },
  {
    number: "02",
    title: "Enforcement",
    subtitle: "Missing paperwork",
    body: "On April 1, 2026, FDA issued its first DSCSA warning letter to a dispenser. The dispenser was a medical spa in Texas. Clinics are now on the inspection map.",
    source: "https://americanmedspa.org/news/fda-warning-letter-to-texas-medical-spa-signals-increased-compliance-enforcement",
    sourceLabel: "American Med Spa Association",
  },
  {
    number: "03",
    title: "Margin",
    subtitle: "One rep, one price list",
    body: "Plenty of practices buy from the same price list for years. Without a second quote from a legitimate source, there’s no way to tell whether you’re overpaying.",
  },
];

const reviewItems = [
  "Every supplier you buy from is a licensed, authorized trading partner",
  "Transaction information and a transaction statement arrive with each order",
  "You keep those records for six years and can produce them on request",
  "You have a written process to quarantine and investigate suspect product",
  "You can notify FDA and your trading partners within 24 hours of finding illegitimate product",
];

function Arrow() {
  return <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m-6-6 6 6-6 6" /></svg>;
}

export default function DrugSourcingLandingPage() {
  const [content, setContent] = useState<LandingPageContent>(
    DEFAULT_LANDING_PAGE_CONTENT["drug-sourcing"],
  );

  useEffect(() => {
    let active = true;
    getUtmParams();
    setLandingAttribution({ landingPage: LP_PATH, variantKey: "control" });
    fetch(`/api/landing-pages/drug-sourcing?visitorId=${encodeURIComponent(getVisitorId())}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("managed page unavailable")))
      .then((data) => {
        if (!active) return;
        if (data?.content) setContent(data.content as LandingPageContent);
        setLandingAttribution({
          landingPage: LP_PATH,
          variantKey: data?.assignment?.variantKey || "control",
          experimentId: data?.assignment?.experimentId || undefined,
        });
        trackLandingEvent("landing_page_view", LP_PATH);
      })
      .catch(() => {
        if (!active) return;
        setLandingAttribution({ landingPage: LP_PATH, variantKey: "control" });
        trackLandingEvent("landing_page_view", LP_PATH);
      });
    const stopTracking = setupPageTimeTracking(LP_PATH);
    return () => { active = false; stopTracking(); };
  }, []);

  const handleCta = (location: string) => {
    trackLandingEvent("landing_page_cta_click", LP_PATH, {
      buttonLabel: content.ctaText,
      buttonLocation: location,
    });
    trackButtonClick(content.ctaText, location);
  };

  const handleBuyerType = (type: string) => {
    trackLandingEvent("landing_page_audience_click", LP_PATH, { audienceType: type });
  };

  return (
    <LandingLayout ctaText={content.ctaText}>
      <Helmet>
        <title>{META_TITLE}</title>
        <meta name="description" content={META_DESCRIPTION} />
        <link rel="canonical" href={`https://premierpharma.com${LP_PATH}`} />
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />
      </Helmet>

      <section className="relative isolate overflow-hidden bg-primary text-white landing-grid">
        <div className="pointer-events-none absolute -right-28 -top-28 h-96 w-96 rounded-full border-[54px] border-accent/15" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid lg:grid-cols-[.94fr_1.06fr] gap-10 lg:gap-14 items-center">
            <div className="relative z-10">
              <p className="inline-flex items-center gap-2 rounded-full border border-emerald-300/30 bg-emerald-300/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[.15em] text-emerald-200">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                Drug sourcing + free DSCSA review
              </p>
              <h1 className="mt-6 max-w-2xl text-4xl sm:text-5xl lg:text-[3.8rem] font-bold leading-[1.03] tracking-[-.045em]">{content.headline}</h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/75">{content.subheadline}</p>
              <Link to="/register" onClick={() => handleCta("hero-cta")} className="mt-7 inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-btn bg-accent px-7 py-4 font-bold text-white shadow-lg shadow-black/20 transition hover:bg-accent-600">
                {content.ctaText}<Arrow />
              </Link>
              <p className="mt-3 text-sm text-white/65">{content.reassurance}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {content.trustSignals.map((signal) => <span key={signal} className="rounded-full border border-white/15 bg-white/[.06] px-3 py-1.5 text-xs font-semibold text-white/80">{signal}</span>)}
              </div>
            </div>

            <div className="relative min-w-0 rounded-2xl border border-white/15 bg-[#f7f6f0] p-4 sm:p-6 text-primary shadow-2xl shadow-black/20">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-primary/10 pb-4">
                <div>
                  <p className="text-xs uppercase tracking-[.16em] text-primary/55">Sourcing quote · Sample</p>
                  <p className="mt-1 text-sm font-bold">A transparent comparison</p>
                </div>
                <span className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-800">Illustrative only</span>
              </div>
              <div className="mt-4 space-y-2">
                <div className="hidden sm:grid grid-cols-[1.45fr_1fr_.65fr_.95fr] gap-2 px-3 text-[10px] font-bold uppercase tracking-wider text-primary/45">
                  <span>Item</span><span>Best authorized source</span><span>Records</span><span>Verdict</span>
                </div>
                {[
                  ["Ondansetron 4 mg/2 mL vial", "Manufacturer direct", "TI + TS", "Beats your price", true],
                  ["Lidocaine HCl 1%, 10 mL vial", "Authorized distributor", "TI + TS", "Beats your price", true],
                  ["Ketorolac 30 mg/mL, 1 mL vial", "Your current supplier", "n/a", "Keep your supplier", false],
                ].map(([item, source, records, verdict, savings]) => (
                  <div key={String(item)} className="grid grid-cols-1 sm:grid-cols-[1.45fr_1fr_.65fr_.95fr] gap-1 sm:gap-2 rounded-xl border border-primary/10 bg-white px-3 py-3 text-xs">
                    <span className="font-bold leading-snug">{item}</span>
                    <span className="text-primary/65 sm:leading-snug"><span className="sm:hidden font-semibold text-primary/45">Source · </span>{source}</span>
                    <span className="text-primary/65"><span className="sm:hidden font-semibold text-primary/45">Records · </span>{records}</span>
                    <span className={`font-bold ${savings ? "text-emerald-700" : "text-primary/55"}`}>{verdict}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-primary/55">TI = transaction information, TS = transaction statement. Both are required under DSCSA.</p>
              <div className="mt-4 flex items-center gap-3 border-t border-primary/10 pt-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">PP</span>
                <p className="text-xs leading-relaxed text-primary/65"><strong className="text-primary">A real second quote.</strong> No obligation to change your supplier.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="lg:hidden sticky top-[4.5rem] z-40 border-b border-[hsl(var(--line))] bg-[hsl(var(--paper)/.96)] px-4 py-3 backdrop-blur">
        <Link to="/register" onClick={() => handleCta("drug-sourcing-mobile-bar")} className="btn-primary inline-flex w-full justify-center py-3">{content.ctaText}</Link>
      </div>

      <section className="bg-[#f3f5f2] py-14 lg:py-20" aria-labelledby="audience-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[.72fr_1.28fr] gap-8 lg:gap-14 items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.16em] text-accent">Wholesale sourcing, made for your practice</p>
              <h2 id="audience-heading" className="mt-3 text-3xl sm:text-4xl font-bold leading-tight tracking-tight text-primary">Who are you buying for?</h2>
            </div>
            <div>
              <div className="flex flex-wrap gap-2.5">
                {buyerTypes.map(({ label, value }) => (
                  <Link key={value} to={`/register?type=${value}`} onClick={() => handleBuyerType(value)} className="rounded-full border border-primary/15 bg-white px-4 py-2.5 text-sm font-semibold text-primary transition hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                    {label}<span className="ml-2 text-accent" aria-hidden="true">↗</span>
                  </Link>
                ))}
              </div>
              <p className="mt-6 max-w-3xl border-l-2 border-accent pl-4 text-sm leading-relaxed text-primary/70">Under the Drug Supply Chain Security Act, anyone authorized to dispense or administer prescription drugs counts as a dispenser. That includes clinics and med spas, not just pharmacies.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" aria-labelledby="risk-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[.16em] text-accent">The cost of getting it wrong</p>
            <h2 id="risk-heading" className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight text-primary">A cheap vial from the wrong source is the most expensive thing you can buy.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {risks.map((risk) => (
              <article key={risk.number} className="flex flex-col border-t-2 border-primary/15 pt-5">
                <span className="font-mono text-xs font-bold text-accent">{risk.number} / {risk.title}</span>
                <h3 className="mt-4 text-xl font-bold text-primary">{risk.subtitle}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-primary/70">{risk.body}</p>
                {risk.source && <a href={risk.source} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex w-fit items-center gap-2 border-b border-primary/20 pb-1 text-xs font-semibold text-primary hover:border-accent hover:text-accent">{risk.sourceLabel}<Arrow /></a>}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="dscsa-review" className="scroll-mt-24 bg-primary py-16 lg:py-24 text-white" aria-labelledby="review-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.16em] text-emerald-300">Free · No purchase required</p>
              <h2 id="review-heading" className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">DSCSA readiness review</h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/75">FDA’s small-dispenser exemption now runs to November 27, 2027. It delays electronic, package-level tracing. It does not delay the rest of the law, and most of the rest already applies to you.</p>
              <p className="mt-7 text-sm font-bold text-white">What we check with you</p>
              <ul className="mt-4 space-y-3">
                {reviewItems.map((item) => <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-white/80"><span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-emerald-300/50 text-emerald-300" aria-hidden="true"><svg className="h-3 w-3" viewBox="0 0 16 16" fill="none"><path d="m3 8 3.2 3.2L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></span>{item}</li>)}
              </ul>
              <div className="mt-8 rounded-xl border border-emerald-300/25 bg-white/[.06] p-5">
                <p className="font-bold">You get a 30-minute call with a pharmacist and a written gap checklist to keep.</p>
                <p className="mt-2 text-sm leading-relaxed text-white/65">We book it after your license is verified.</p>
                <Link to="/register" onClick={() => handleCta("readiness-review-cta")} className="mt-5 inline-flex items-center gap-2 rounded-btn bg-accent px-5 py-3 font-bold text-white transition hover:bg-accent-600">{content.ctaText}<Arrow /></Link>
              </div>
            </div>
            <div className="mx-auto w-full max-w-md rotate-[1deg] rounded-sm bg-[#f5f1e5] p-5 sm:p-7 text-primary shadow-2xl">
              <div className="flex items-start justify-between gap-3 border-b border-primary/20 pb-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[.18em] text-primary/50">Sample record</p>
                  <h3 className="mt-1 font-bold">Transaction information</h3>
                </div>
                <span className="rounded-full border border-emerald-800/25 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-900">Keep 6 years</span>
              </div>
              <dl className="mt-4 space-y-3 font-mono text-xs">
                {[
                  ["Product name", "Ondansetron injection"],
                  ["Strength / dosage form", "4 mg / 2 mL vial"],
                  ["National Drug Code", "NDC recorded per package"],
                  ["Lot number", "Lot recorded"],
                  ["Transaction date", "Date of transfer"],
                  ["Quantity", "Quantity transferred"],
                  ["Container size / count", "10 vials per carton / 1 carton"],
                  ["Shipment date", "Recorded when different from transaction date"],
                  ["Seller", "Business name and address"],
                  ["Buyer", "Business name and address"],
                ].map(([label, value]) => <div key={label} className="flex flex-col gap-1 border-b border-dashed border-primary/15 pb-2 sm:flex-row sm:justify-between"><dt className="text-primary/55">{label}</dt><dd className="font-semibold sm:text-right">{value}</dd></div>)}
              </dl>
              <p className="mt-4 text-[10px] leading-relaxed text-primary/55">Illustrative example only. Required fields and records depend on transaction and applicable DSCSA requirements.</p>
            </div>
          </div>
        </div>
      </section>

      <HowItWorks content={content} onCtaClick={() => handleCta("how-it-works-cta")} />

      <section className="bg-[#f3f5f2] py-16 lg:py-20" aria-labelledby="founder-heading">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-[.7fr_1.3fr] gap-8 lg:gap-14 items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.16em] text-accent">Pharmacy-led. Idaho-based.</p>
              <h2 id="founder-heading" className="mt-3 text-3xl sm:text-4xl font-bold leading-tight text-primary">Run by a pharmacist</h2>
              <p className="mt-4 text-sm leading-relaxed text-primary/65">Premier Pharma is a licensed, NABP-accredited wholesale distributor in Boise, Idaho, founded by Jacob Fuchs, PharmD.</p>
            </div>
            <figure className="border-l-2 border-accent pl-6 sm:pl-8">
              <blockquote className="text-xl sm:text-2xl leading-relaxed tracking-tight text-primary">“The pharmaceutical industry has an incentive problem. When the people selling you drugs make more money the more you pay, something is fundamentally misaligned. We built Premier Pharma to fix that.”</blockquote>
              <figcaption className="mt-5 text-sm font-bold text-primary">Jacob Fuchs <span className="font-normal text-primary/55">· Founder, Premier Pharma</span></figcaption>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="https://www.ibtimes.com/jacob-fuchs-software-engineerpharmacist-shaping-connected-healthcare-vision-3795303" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-accent underline underline-offset-4">International Business Times</a>
                <a href="https://www.medicaldaily.com/incentive-problem-healthcare-jacob-fuchss-journey-fix-it-474505" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-accent underline underline-offset-4">Medical Daily</a>
              </div>
            </figure>
          </div>
        </div>
      </section>

      <ManagedContentSections content={content} pageKind="drug-sourcing" />

      <section className="bg-primary py-16 lg:py-24 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[.16em] text-emerald-300">Source with confidence</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">Get a straight answer on what you’re paying.</h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/75">Free account, sourcing quote and DSCSA readiness review for licensed practices. No contract. No purchase required.</p>
          <Link to="/register" onClick={() => handleCta("final-drug-sourcing-cta")} className="mt-8 inline-flex items-center justify-center gap-3 rounded-btn bg-accent px-8 py-4 font-bold text-white transition hover:bg-accent-600">{content.ctaText}<Arrow /></Link>
          <p className="mt-5 text-sm text-white/70">or call <a href="tel:2086390241" onClick={() => trackLandingEvent("landing_page_phone_click", LP_PATH, { buttonLocation: "final-cta" })} className="font-bold text-white underline underline-offset-4">208-639-0241</a></p>
          <p className="mx-auto mt-8 max-w-3xl text-[11px] leading-relaxed text-white/50">Sales are limited to licensed healthcare businesses and require credential verification. The DSCSA readiness review is general operational guidance, not legal advice.</p>
        </div>
      </section>
    </LandingLayout>
  );
}
